import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  FileText, Wand2, Map, CheckSquare, CalendarClock, ArrowRight,
  BookOpen, Car, CreditCard, Building2, GraduationCap, FolderOpen, Clock,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  immigration: BookOpen, social_security: CreditCard, dmv: Car,
  tax: Building2, education: GraduationCap,
};

const actionIcons = [FileText, Map, FolderOpen, Clock, CheckSquare, CalendarClock];
const actionRoutes = ["/forms", "/roadmaps", "/documents", "/timeline", "/checklists", "/deadlines"];
const actionColors = [
  "bg-teal-50 text-teal-700 border-teal-200",
  "bg-sky-50 text-sky-700 border-sky-200",
  "bg-violet-50 text-violet-700 border-violet-200",
  "bg-indigo-50 text-indigo-700 border-indigo-200",
  "bg-emerald-50 text-emerald-700 border-emerald-200",
  "bg-amber-50 text-amber-700 border-amber-200",
];

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = i18n.language?.split("-")[0] || "en";
  const [forms, setForms] = useState([]);
  const [wizards, setWizards] = useState([]);
  const [loading, setLoading] = useState(true);

  const actions = t("dashboard.actions", { returnObjects: true });

  useEffect(() => {
    const load = async () => {
      try {
        const [formsRes, wizardsRes] = await Promise.all([
          axios.get(`${API}/forms`, { params: { locale } }),
          axios.get(`${API}/wizard`, { withCredentials: true }),
        ]);
        setForms(formsRes.data);
        setWizards(wizardsRes.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [locale]);

  const activeWizards = wizards.filter((w) => w.status === "in_progress");

  return (
    <main className="min-h-screen bg-background" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-heading mb-2" data-testid="dashboard-welcome">
            {t("dashboard.welcome")}{user?.name?.split(" ")[0] || ""}
          </h1>
          <p className="text-base text-slate-500">{t("dashboard.subtitle")}</p>
        </div>

        {/* Active wizards */}
        {activeWizards.length > 0 && (
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">{t("dashboard.continueTitle")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activeWizards.slice(0, 4).map((w) => (
                <Link to={`/wizard/${w.form_id}?session=${w.session_id}`} key={w.session_id}>
                  <Card className="border-teal-200/50 hover:border-teal-300 hover:shadow-md transition-all duration-300" data-testid={`active-wizard-${w.session_id}`}>
                    <CardContent className="p-5 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-teal-50 flex items-center justify-center">
                          <Wand2 className="h-5 w-5 text-teal-700" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{w.form_name}</p>
                          <p className="text-xs text-slate-500">{t("dashboard.step")} {w.current_step} {t("dashboard.of")} {w.total_steps}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-teal-500 rounded-full" style={{ width: `${(w.current_step / w.total_steps) * 100}%` }} />
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Quick actions */}
        <div className="mb-10">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">{t("dashboard.quickActionsTitle")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.isArray(actions) && actions.map((action, i) => {
              const Icon = actionIcons[i];
              return (
                <Link to={actionRoutes[i]} key={actionRoutes[i]}>
                  <Card className={`border hover:shadow-md transition-all duration-300 cursor-pointer card-hover ${actionColors[i]}`} data-testid={`quick-action-${i}`}>
                    <CardContent className="p-5">
                      <Icon className="h-8 w-8 mb-3" />
                      <h3 className="text-sm font-semibold mb-1">{action.label}</h3>
                      <p className="text-xs opacity-80">{action.desc}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Available forms */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">{t("dashboard.availableForms")}</h2>
            <Link to="/forms">
              <Button variant="ghost" className="text-teal-700 text-sm font-medium" data-testid="view-all-forms">
                {t("dashboard.viewAll")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="border-slate-100 animate-pulse">
                  <CardContent className="p-6"><div className="h-4 bg-slate-200 rounded w-3/4 mb-3" /><div className="h-3 bg-slate-100 rounded w-full" /></CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {forms.map((form) => {
                const Icon = categoryIcons[form.category] || FileText;
                return (
                  <Link to={`/forms/${form.form_id}`} key={form.form_id}>
                    <Card className="border-slate-100 hover:border-teal-200/50 hover:shadow-md transition-all duration-300 card-hover" data-testid={`form-card-${form.form_id}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="h-10 w-10 rounded-lg bg-slate-50 flex items-center justify-center">
                            <Icon className="h-5 w-5 text-slate-600" />
                          </div>
                          <Badge variant="outline" className="text-xs capitalize">{form.category?.replace("_", " ")}</Badge>
                        </div>
                        <h3 className="text-sm font-semibold text-slate-900 mb-1">{form.code} - {form.name}</h3>
                        <p className="text-xs text-slate-500 line-clamp-2 mb-3">{form.description}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-400">
                          <span>{form.estimated_time}</span>
                          <span>Fee: {form.filing_fee}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
